import React, { useState, useCallback } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import { pickImageFromCamera, pickImageWithChoice } from "./imagePicker";
import {
  submitKycFromContext,
  uploadOnboardingImage,
  getBlockedErrorMessage,
} from "@services/onboardingApi";
import {
  commonStyles,
  vehicleStyles as styles,
  placeholderColor,
} from "./styles";
import { useOnboarding } from "./OnboardingContext";
import { useFlowNavigation } from "./useFlowNavigation";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList, DocumentUpload } from "./types";

interface YourDocumentsProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "YourDocuments">;
}

type DocKey = "aadhaarCard" | "panCard" | "drivingLicense";

interface YourDocumentsForm {
  aadhaarCard: DocumentUpload;
  panCard: DocumentUpload;
  drivingLicense: DocumentUpload;
  photo: string | null;
}

const INITIAL_FORM: YourDocumentsForm = {
  aadhaarCard: {},
  panCard: {},
  drivingLicense: {},
  photo: null,
};

type UploadKey =
  | "aadhaarCard:front"
  | "aadhaarCard:back"
  | "panCard:front"
  | "panCard:back"
  | "drivingLicense:front"
  | "drivingLicense:back"
  | "photo";

const YourDocuments: React.FC<YourDocumentsProps> = ({ navigation }) => {
  const { data: onboardingData, updateData } = useOnboarding();
  const { goToNextStep, isLagganFlow, getDocumentsConfig } =
    useFlowNavigation();
  const [form, setForm] = useState<YourDocumentsForm>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Set<UploadKey>>(new Set());
  const [localUris, setLocalUris] = useState<
    Partial<Record<UploadKey, string>>
  >({});

  const phoneNumber = onboardingData.phoneNumber;
  const [personalDocNumber, setPersonalDocNumber] = useState("");

  const hasAadhaar = !!form.aadhaarCard.front;
  const hasPan = !!form.panCard.front;
  const hasDl = !!form.drivingLicense.front;
  const hasAnyDoc = hasAadhaar || hasPan || hasDl;

  const isFormValid =
    hasAnyDoc && !!form.photo && personalDocNumber.trim().length > 0;

  const setUploading = useCallback((key: UploadKey, on: boolean) => {
    setUploadingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const uploadAsset = useCallback(
    async (asset: {
      uri?: string;
      fileName?: string | null;
      type?: string | null;
    }): Promise<string | null> => {
      if (!asset.uri || !phoneNumber) return null;
      try {
        return await uploadOnboardingImage(
          {
            uri: asset.uri,
            name: asset.fileName ?? `upload_${Date.now()}`,
            type: asset.type ?? "image/jpeg",
          },
          phoneNumber,
        );
      } catch (err) {
        const axiosError = err as {
          response?: { data?: { message?: string } };
        };
        toast.showLongToast(
          axiosError?.response?.data?.message || "Upload failed. Try again.",
        );
        return null;
      }
    },
    [phoneNumber],
  );

  const pickImage = useCallback(
    async (documentKey: DocKey | "photo", side?: "front" | "back") => {
      try {
        const asset = await pickImageWithChoice();
        if (!asset) return;

        const key: UploadKey =
          documentKey === "photo"
            ? "photo"
            : (`${documentKey}:${side ?? "front"}` as UploadKey);
        if (asset.uri) setLocalUris((prev) => ({ ...prev, [key]: asset.uri }));
        setUploading(key, true);
        const url = await uploadAsset(asset);
        setUploading(key, false);
        if (!url) return;

        if (documentKey === "photo") {
          setForm((prev) => ({ ...prev, photo: url }));
        } else if (side) {
          setForm((prev) => ({
            ...prev,
            [documentKey]: { ...prev[documentKey], [side]: url },
          }));
        }
      } catch {
        toast.showLongToast("Failed to pick image. Please try again.");
      }
    },
    [setUploading, uploadAsset],
  );

  const takePhoto = useCallback(async () => {
    try {
      const asset = await pickImageFromCamera("front");
      if (!asset) return;

      if (asset.uri) setLocalUris((prev) => ({ ...prev, photo: asset.uri }));
      setUploading("photo", true);
      const url = await uploadAsset(asset);
      setUploading("photo", false);
      if (!url) return;

      setForm((prev) => ({ ...prev, photo: url }));
    } catch {
      toast.showLongToast("Failed to open camera. Please try again.");
    }
  }, [setUploading, uploadAsset]);

  const resolvePersonalDoc = useCallback(() => {
    // Priority: Aadhaar > PAN > DL. Front is required; back is optional.
    if (hasAadhaar) return { type: "AADHAAR" as const, doc: form.aadhaarCard };
    if (hasPan) return { type: "PAN" as const, doc: form.panCard };
    if (hasDl) return { type: "DL" as const, doc: form.drivingLicense };
    return null;
  }, [hasAadhaar, hasPan, hasDl, form]);

  const onContinue = useCallback(async () => {
    if (isSubmitting || !isFormValid) return;

    const picked = resolvePersonalDoc();
    const nextData = {
      ...onboardingData,
      personalDocType: picked?.type ?? null,
      personalDocNumber: personalDocNumber.trim(),
      personalDocPhotos: picked?.doc ?? {},
      // Photo on this screen is the user's selfie (executive verification).
      // Don't override the one captured during Documents.tsx if already set.
      selfieUrl: onboardingData.selfieUrl ?? form.photo,
      // legacy mirror
      personalAadhaarPhotos: picked?.doc ?? {},
    };
    updateData({
      personalDocType: nextData.personalDocType,
      personalDocNumber: nextData.personalDocNumber,
      personalDocPhotos: nextData.personalDocPhotos,
      selfieUrl: nextData.selfieUrl,
      personalAadhaarPhotos: nextData.personalDocPhotos,
      selfieUri: nextData.selfieUrl,
    });

    if (isLagganFlow()) {
      // Laggan defers KYC submit to AddressDetails — just advance
      goToNextStep();
      return;
    }

    // Family KYC + normal DB → submit and advance to SignUpSuccessful
    setIsSubmitting(true);
    try {
      await submitKycFromContext(nextData, getDocumentsConfig());
      goToNextStep();
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      if (msg.startsWith("MISSING:")) {
        toast.showLongToast(`Missing ${msg.replace("MISSING:", "")}`);
      } else {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        toast.showLongToast(
          getBlockedErrorMessage(err) ||
            axiosErr?.response?.data?.message ||
            "Failed to submit KYC. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isFormValid,
    form,
    personalDocNumber,
    onboardingData,
    resolvePersonalDoc,
    updateData,
    goToNextStep,
    isLagganFlow,
    getDocumentsConfig,
  ]);

  const renderUploadBox = (documentKey: DocKey, side: "front" | "back") => {
    const value = form[documentKey][side];
    const label = side === "front" ? "Upload Front" : "Upload Back";
    const isUploaded = !!value;
    const uploadKey = `${documentKey}:${side}` as UploadKey;
    const isUploading = uploadingKeys.has(uploadKey);
    const localUri = localUris[uploadKey];

    return (
      <Pressable
        style={[styles.uploadBox, isUploaded && styles.uploadBoxFilled]}
        onPress={() => !isUploading && pickImage(documentKey, side)}
        accessibilityRole="button"
        accessibilityLabel={`${label} for ${documentKey}`}
      >
        {isUploading ? (
          <ActivityIndicator color={color.primary} />
        ) : isUploaded && localUri ? (
          <>
            <Image
              source={{ uri: localUri }}
              style={ydStyles.thumbnail}
              resizeMode="cover"
            />
            <View style={ydStyles.thumbnailBadge}>
              <Text style={ydStyles.thumbnailBadgeText}>✓</Text>
            </View>
          </>
        ) : isUploaded ? (
          <>
            <Text style={styles.uploadCheckmark}>✓</Text>
            <Text style={styles.uploadedFileName} numberOfLines={2}>
              Uploaded
            </Text>
          </>
        ) : (
          <>
            <Text style={styles.uploadBoxIcon}>↑</Text>
            <Text style={styles.uploadText}>{label}</Text>
          </>
        )}
      </Pressable>
    );
  };

  const renderDocumentSection = (documentKey: DocKey, label: string) => (
    <View key={documentKey} style={styles.documentSection}>
      <Text style={styles.documentLabel}>
        {label}
        <Text style={styles.requiredStar}> (any one)</Text>
      </Text>
      <View style={styles.uploadRow}>
        {renderUploadBox(documentKey, "front")}
        {renderUploadBox(documentKey, "back")}
      </View>
    </View>
  );

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Your Documents"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Text
          variant="title_16"
          style={[styles.sectionTitle, styles.yourDocsSectionTitle]}
        >
          Your Documents
        </Text>
        <Text variant="body_text_3" style={styles.yourDocsHelperText}>
          Please upload any one of your personal ID card
        </Text>

        {renderDocumentSection("aadhaarCard", "Aadhaar Card")}
        {renderDocumentSection("panCard", "Pan Card")}
        {renderDocumentSection("drivingLicense", "Driving License")}

        {hasAnyDoc && (
          <View style={styles.documentSection}>
            <Text style={styles.documentLabel}>
              Document Number<Text style={styles.requiredStar}>*</Text>
            </Text>
            <View style={commonStyles.inputContainer}>
              <TextInput
                value={personalDocNumber}
                style={commonStyles.inputText}
                placeholder={
                  hasAadhaar
                    ? "Enter Aadhaar Number"
                    : hasPan
                      ? "Enter PAN Number"
                      : "Enter DL Number"
                }
                placeholderTextColor={placeholderColor}
                onChangeText={setPersonalDocNumber}
                autoCapitalize="characters"
                accessibilityLabel="Personal document number"
              />
            </View>
          </View>
        )}

        {/* Photo */}
        <View style={styles.documentSection}>
          <Text style={styles.documentLabel}>
            Upload Photo<Text style={styles.requiredStar}>*</Text>
          </Text>
          <Pressable
            style={[
              styles.photoUploadBox,
              form.photo && styles.uploadBoxFilled,
            ]}
            onPress={takePhoto}
            accessibilityRole="button"
            accessibilityLabel="Take a photo"
          >
            {uploadingKeys.has("photo") ? (
              <ActivityIndicator color={color.primary} />
            ) : form.photo && localUris.photo ? (
              <>
                <Image
                  source={{ uri: localUris.photo }}
                  style={ydStyles.thumbnail}
                  resizeMode="cover"
                />
                <View style={ydStyles.thumbnailBadge}>
                  <Text style={ydStyles.thumbnailBadgeText}>✓</Text>
                </View>
              </>
            ) : form.photo ? (
              <>
                <Text style={styles.uploadCheckmark}>✓</Text>
                <Text style={styles.uploadedFileName}>Photo uploaded</Text>
              </>
            ) : (
              <>
                <Text style={styles.uploadEmoji}>📷</Text>
                <Text style={styles.uploadText}>Take Photo</Text>
              </>
            )}
          </Pressable>
        </View>
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[
            commonStyles.primaryButton,
            !isFormValid && commonStyles.primaryButtonDisabled,
          ]}
          disabled={isSubmitting || !isFormValid}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text variant="title_16" style={commonStyles.primaryButtonText}>
            Continue
          </Text>
        </Pressable>
      </View>
    </>
  );
};

const ydStyles = StyleSheet.create({
  thumbnail: {
    ...StyleSheet.absoluteFill,
    borderRadius: 7,
  },
  thumbnailBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: color.plain_green,
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailBadgeText: {
    color: color.white,
    fontSize: 11,
    lineHeight: 13,
  },
});

export default YourDocuments;
